/**
 * Provider Directory Results Component
 * Displays search results as cards with key information
 */

import { MapPin, Phone, Globe, Star, Navigation, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/button';

export interface Provider {
  id: string;
  organization_name: string;
  organization_type: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  primary_phone: string;
  primary_email?: string;
  website?: string;
  latitude?: number;
  longitude?: number;
  distance_miles?: number;
  data_quality_score?: number;
  services: string[];
  languages_spoken: string[];
  accepts_medicaid: boolean;
  accepts_medicare: boolean;
  hours_of_operation?: any;
  wheelchair_accessible?: boolean;
  status: string;
}

interface ProviderResultsProps {
  providers: Provider[];
  isLoading?: boolean;
  onProviderClick: (provider: Provider) => void;
  onGetDirections: (provider: Provider) => void;
  onSaveProvider?: (provider: Provider) => void;
  isProviderSaved?: (providerId: string) => boolean;
}

export function ProviderResults({
  providers,
  isLoading = false,
  onProviderClick,
  onGetDirections,
  onSaveProvider,
  isProviderSaved
}: ProviderResultsProps) {

  if (isLoading) {
    return (
      <Card className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
        <CardContent className="flex flex-col items-center justify-center space-y-4">
          <LoadingSpinner size="lg" text="Searching for providers..." />
        </CardContent>
      </Card>
    );
  }

  if (providers.length === 0) {
    return (
      <EmptyState
        icon={<MapPin className="w-8 h-8 text-gray-400" />}
        title="No providers found"
        message="Try adjusting your search location, increasing the radius, or removing some filters."
      />
    );
  }

  const getQualityColor = (score?: number) => {
    if (!score) return 'text-gray-500';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-orange-600';
  };

  const getQualityLabel = (score?: number) => {
    if (!score) return 'Not rated';
    if (score >= 80) return 'High quality';
    if (score >= 60) return 'Good quality';
    return 'Needs update';
  };

  return (
    <div className="space-y-4">
      {/* Results Header */}
      <Card className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <CardContent className="p-4">
          <h3 className="text-heading-3 text-gray-900 dark:text-gray-100">
            {providers.length} {providers.length === 1 ? 'Provider' : 'Providers'} Found
          </h3>
          <p className="text-body text-gray-600 dark:text-gray-400">
            {providers.some(p => p.distance_miles != null) ? 'Sorted by distance' : 'Sorted by name'}
          </p>
        </CardContent>
      </Card>

      {/* Provider Cards */}
      {providers.map((provider) => (
        <Card
          key={provider.id}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 dark:border-gray-700"
          onClick={() => onProviderClick(provider)}
        >
          <CardContent className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {provider.organization_name}
                </h4>
                <span className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full">
                  {typeof provider.organization_type === 'string'
                    ? provider.organization_type
                    : typeof provider.organization_type === 'object'
                    ? JSON.stringify(provider.organization_type)
                    : 'Unknown'}
                </span>
              </div>
              {provider.distance_miles != null && (
                <div className="text-right ml-4">
                  <div className="text-2xl font-bold text-blue-600">
                    {provider.distance_miles.toFixed(1)}
                  </div>
                  <div className="text-sm text-gray-500">miles</div>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div className="space-y-2 mb-4">
              {/* Address */}
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="flex flex-col text-sm">
                  <span className="text-gray-900 dark:text-gray-100">{provider.street_address}</span>
                  <span className="text-gray-600">
                    {provider.city}, {provider.state} {provider.zip_code}
                  </span>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <a
                  href={`tel:${provider.primary_phone}`}
                  className="text-sm text-blue-600 hover:text-blue-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  {provider.primary_phone}
                </a>
              </div>

              {/* Website */}
              {provider.website && (
                <div className="flex items-center space-x-2">
                  <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a
                    href={provider.website.startsWith('http') ? provider.website : `https://${provider.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Visit Website
                  </a>
                </div>
              )}
            </div>

            {/* Services */}
            {provider.services && Array.isArray(provider.services) && provider.services.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Services Offered</h5>
                <div className="flex flex-wrap gap-2">
                  {provider.services.slice(0, 5).map((service, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded"
                    >
                      {typeof service === 'string' ? service : String(service)}
                    </span>
                  ))}
                  {provider.services.length > 5 && (
                    <span className="px-2 py-1 text-xs font-medium text-gray-500">
                      +{provider.services.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Key Features */}
            <div className="flex flex-wrap gap-3 mb-4">
              {provider.accepts_medicaid && (
                <div className="flex items-center space-x-1 text-sm text-green-700">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Medicaid</span>
                </div>
              )}
              {provider.accepts_medicare && (
                <div className="flex items-center space-x-1 text-sm text-green-700">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Medicare</span>
                </div>
              )}
              {provider.wheelchair_accessible && (
                <div className="flex items-center space-x-1 text-sm text-green-700">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Wheelchair Accessible</span>
                </div>
              )}
              {provider.languages_spoken && provider.languages_spoken.length > 1 && (
                <div className="flex items-center space-x-1 text-sm text-blue-700">
                  <Globe className="w-4 h-4 flex-shrink-0" />
                  <span>{provider.languages_spoken.length} Languages</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
              {/* Quality Score */}
              <div className="flex items-center space-x-2">
                <Star className={`w-4 h-4 flex-shrink-0 ${getQualityColor(provider.data_quality_score)}`} />
                <span className={`text-sm font-medium ${getQualityColor(provider.data_quality_score)}`}>
                  {getQualityLabel(provider.data_quality_score)}
                </span>
                {provider.data_quality_score && (
                  <span className="text-xs text-gray-500">
                    ({provider.data_quality_score}/100)
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                {/* Save Provider Button */}
                {onSaveProvider && isProviderSaved && (
                  <Button
                    variant={isProviderSaved(provider.id) ? "outline" : "outline"}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSaveProvider(provider);
                    }}
                    className={
                      isProviderSaved(provider.id)
                        ? 'bg-green-100 text-green-700 border-2 border-green-500 hover:bg-green-200'
                        : 'text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
                    }
                    icon={<CheckCircle className={`w-4 h-4 flex-shrink-0 ${
                      isProviderSaved(provider.id) ? 'fill-green-700' : ''
                    }`} />}
                    title={isProviderSaved(provider.id) ? 'Saved to My Providers' : 'Save to My Providers'}
                  >
                    {isProviderSaved(provider.id) ? 'Saved' : 'Save'}
                  </Button>
                )}

                {/* Get Directions Button */}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onGetDirections(provider);
                  }}
                  icon={<Navigation className="w-4 h-4 flex-shrink-0" />}
                >
                  Directions
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
